import { useTranslation } from 'react-i18next';
import React, { useMemo, useEffect, useCallback } from 'react';
import cx, { clsx } from 'clsx';

import { IconRcTick } from '@/assets';
// import { IconRcStreamShare } from '@/assets/stream';
import { Modal, UnionFollowButton, UnionAvatar, Button, Tips } from '@/components';
import { useUserPermission, useCurrentAddressInfo } from '@/hooks';
import {
  useFeedCanJoin,
  useFeedJoinDraw,
  useArticleRepost,
  FeedArticle,
  invalidStreamDrawCanJoin,
} from '@/views/Stream';

import { DrawToken } from '../DrawToken';
import { InteractPermissions } from '../InteractPermissions';
import style from './index.module.scss';
import { useUserTrial } from '@/hooks/trial';
import { streamActionToast } from '@/components/SystemErrorToast';

const { CommonModal } = Modal;

export const JoinDrawModal = ({
  article,
  on,
  turnOff,
}: {
  article: FeedArticle;
  on?: boolean;
  turnOff?: () => void;
}) => {
  const { t } = useTranslation();

  const { address } = useCurrentAddressInfo();
  const drawId = useMemo(() => article.draw!.id, [article.draw]);
  const { data: canJoin } = useFeedCanJoin(drawId);
  const {
    data: permissions,
    mutate,
    isSuccess: isPermissionLoaded,
  } = useUserPermission('feed_draw');
  const { data: trialInfo, isSuccess: isTrialInfoLoaded } = useUserTrial('JOIN_LUCKY_DRAW');

  const { isLoading, mutate: joinDraw, data: joinRes } = useFeedJoinDraw(article.id, drawId);
  const { mutateAsync: repostArticle, isLoading: isReposting } = useArticleRepost(article.id);
  const needOperations = useMemo(
    () => article.draw?.operations && !!Object.keys(article.draw?.operations).length,
    [article.draw?.operations],
  );

  useEffect(() => {
    mutate({ draw_id: drawId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRepostClick = useCallback(async () => {
    try {
      await repostArticle();
      invalidStreamDrawCanJoin(drawId, address);
    } catch (error) {}
  }, [repostArticle, drawId, address]);

  const handleFollowChange = useCallback(
    () => invalidStreamDrawCanJoin(drawId, address),
    [drawId, address],
  );

  const isQualified = useMemo(() => permissions?.has_permission, [permissions?.has_permission]);

  const JoinSteps = useMemo(() => {
    return [
      {
        label: (
          <>
            <div className={style.joinStepLabel}>
              <span>{t('JoinDrawModal.1usfh8u')}</span>
              <UnionAvatar className={style.avatar} size={22} data={article.creator} />
              <div className={style.author}>{article._author}</div>
            </div>
            <UnionFollowButton
              type={isQualified && !article.creator!.is_following ? 'primary' : 'gray'}
              address={article.creator!.id}
              ghost={false}
              isFollowing={article.creator!.is_following}
              className={cx(
                style.joinStepBtn,
                article.creator!.is_following
                  ? isQualified
                    ? style.isSuccess
                    : style.isDone
                  : !isQualified && style.isDisabled,
              )}
              operateCallback={handleFollowChange}
              disabled={!isQualified || article.creator!.is_following}
              prefix={
                article.creator!.is_following ? <IconRcTick className={style.tickIcon} /> : null
              }
            />
          </>
        ),
        needed: 'follow_creator' in article.draw!.operations,
        qualified: canJoin?.can_join || canJoin?.details.follow_creator,
      },
      {
        label: (
          <>
            <div className={style.joinStepLabel}>{t('JoinDrawModal.12io0bv')}</div>
            <Button
              onClick={handleRepostClick}
              disabled={!isQualified || article.is_reposted}
              type={isQualified && !article.is_reposted ? 'primary' : 'gray'}
              className={cx(
                style.joinStepBtn,
                article.is_reposted
                  ? isQualified
                    ? style.isSuccess
                    : style.isDone
                  : !isQualified && style.isDisabled,
              )}
              loading={isReposting}>
              {article.is_reposted ? <IconRcTick className={style.tickIcon} /> : null}
              {article.is_reposted ? t('JoinDrawModal.reposted') : t('JoinDrawModal.repost')}
            </Button>
          </>
        ),
        needed: 'repost_article' in article.draw!.operations,
        qualified: canJoin?.can_join || canJoin?.details.repost_article,
      },
    ].filter(x => x.needed);
  }, [article, canJoin, handleRepostClick, handleFollowChange, isReposting, isQualified, t]);

  const handleJoinClick = useCallback(async () => {
    if (trialInfo && !trialInfo?.is_active) {
      streamActionToast('luckDraw');
      return;
    }
    joinDraw();
  }, [joinDraw, trialInfo]);

  return (
    <CommonModal
      on={on}
      containerClassName={clsx(style.joinDraw, joinRes?.inner_id && style.min)}
      turnOff={turnOff}>
      {joinRes?.inner_id ? (
        <div className={style.successContainer}>
          <div className={style.drawSuccessBanner} />
          <div className={style.drawSuccessTitle}>{t('JoinDrawModal.successTitle')}</div>
          <div className={style.tokenDesc}>
            <DrawToken size={26} className={style.drawToken}>
              {joinRes?.inner_id}
            </DrawToken>{' '}
            <span>{t('JoinDrawModal.aeualw')}</span>
            <Tips title={t('JoinDrawModal.m14cki')} size={10} />
          </div>
          {/* <Button className={style.shareBtn} type="primary" ghost>
            <IconRcStreamShare className={style.shareIcon} />
            Share to stream
          </Button> */}
        </div>
      ) : (
        <>
          <div className={style.title}>{t('JoinDrawModal.dptd29')}</div>
          <InteractPermissions
            subTitle={t('JoinDrawModal.permissionSubTitle')}
            userSelfPermissions={permissions}
            postRequiredPermissions={article.draw!.permissions}
            type="luckDraw"
          />
          {needOperations ? (
            <>
              <div className={style.subTitle}>{t('JoinDrawModal.6i7j6c')}</div>
              <div className={style.winnerDesc}>{t('JoinDrawModal.1xtr9c4')}</div>
              {JoinSteps?.map((x, i) => (
                <div className={style.joinStep} key={i}>
                  <div className={style.joinStepIcon}>{i + 1}</div>
                  <div className={style.joinStepDetail}>{x.label}</div>
                </div>
              ))}
            </>
          ) : null}
          <Button
            loading={isLoading}
            disabled={
              !isPermissionLoaded || !isQualified || !canJoin?.can_join || !isTrialInfoLoaded
            }
            onClick={handleJoinClick}
            type="primary"
            className={cx(style.submitBtn, !needOperations && style.emptyOp)}>
            {t('JoinDrawModal.dptd29')}
          </Button>
        </>
      )}
    </CommonModal>
  );
};
